"use client";

import { Link, usePathname } from "@/i18n/navigation";

interface Item {
    href: string;
    label: string;
    external?: boolean;
}

function LabFace() {
    return (
        <span aria-hidden className="lab-face font-mono">
            <span className="lab-face-open">◉_◉</span>
            <span className="lab-face-blink">-_-</span>
        </span>
    );
}

function isActive(pathname: string, href: string): boolean {
    if (href === "/") return pathname === "/";
    return pathname === href || pathname.startsWith(href + "/");
}

export default function SideNav({ items, label }: { items: Item[]; label: string }) {
    const pathname = usePathname();
    if (items.length === 0) return null;

    return (
        <aside
            aria-label={label}
            className="hidden xl:block xl:fixed xl:left-[max(1.5rem,calc((100vw-42rem)/2-12rem))] xl:top-24 xl:w-44"
        >
            <nav>
                <ul className="space-y-1.5 border-l border-border">
                    {items.map((it) => {
                        const active = !it.external && isActive(pathname, it.href);
                        const highlight = it.href === "/labs" && !active;
                        const cls = `-ml-px inline-flex items-center gap-1.5 border-l-2 pl-3 text-sm leading-snug transition-colors ${
                            active
                                ? "border-foreground text-foreground"
                                : "border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground"
                        }`;
                        const content = (
                            <>
                                <span>{it.label}</span>
                                {highlight && <LabFace />}
                            </>
                        );
                        return (
                            <li key={it.href}>
                                {it.external ? (
                                    <a
                                        href={it.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={cls}
                                    >
                                        {content}
                                    </a>
                                ) : (
                                    <Link
                                        href={it.href}
                                        className={cls}
                                        aria-current={active ? "page" : undefined}
                                    >
                                        {content}
                                    </Link>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </nav>
        </aside>
    );
}
